import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { coll } from "@/lib/ddb";
import { useObject, useProject } from "@/lib/ddb-hooks";
import type { Quote, QuoteStatus } from "@/lib/types";

const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" });

export function QuotePanel() {
  const { id, quoteId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const projectId = id ? Number(id) : null;
  const qNum = quoteId ? Number(quoteId) : null;

  const { data: project } = useProject(projectId);
  const { data: quote, isLoading, error } = useObject<Quote>("quotes", qNum);

  // Inject print stylesheet once so window.print() formats the quote
  // page only (no chrome/UI, A4-ish layout).
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-quote-print", "");
    style.innerHTML = PRINT_CSS;
    document.head.appendChild(style);
    return () => { style.remove(); };
  }, []);

  if (!projectId || !qNum) return <div className="empty-state">Invalid quote URL.</div>;
  if (isLoading) return <div className="empty-state">Loading quote…</div>;
  if (error) return <div className="empty-state">Couldn't load quote: {error.message}</div>;
  if (!quote || !project) return <div className="empty-state">Quote not found.</div>;

  async function setStatus(s: QuoteStatus) {
    if (!quote) return;
    await coll("quotes").update<Quote>(quote._id, { status: s, updatedAt: new Date().toISOString() });
    void qc.invalidateQueries({ queryKey: ["obj", "quotes", quote._id] });
  }

  const b = quote.breakdown;

  return (
    <div className="quote-root">
      {/* Toolbar — hidden when printing */}
      <div className="quote-toolbar no-print" style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 24px", borderBottom: "1px solid var(--line)",
      }}>
        <span className="muted">Quote {quote.revision} · {project.name}</span>
        <StatusChip status={quote.status} />
        <span style={{ flex: 1 }} />
        <select
          value={quote.status}
          onChange={(e) => setStatus(e.target.value as QuoteStatus)}
          style={{ width: 140 }}
        >
          <option value="draft">Draft</option>
          <option value="sent">Sent</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="expired">Expired</option>
        </select>
        <button className="ghost" onClick={() => nav(`/projects/${projectId}/bom`)}>← Back to BOM</button>
        <button onClick={() => window.print()}>Print / Save PDF</button>
      </div>

      {/* Printable area */}
      <div className="quote-page">
        <header className="quote-header">
          <div>
            <div className="brand-line">CABINET STUDIO</div>
            <div className="subtitle">Custom cabinetry · USD pricing</div>
          </div>
          <div className="quote-meta">
            <div><b>Quote</b> #{quote._id} · {quote.revision}</div>
            <div>Issued {DATE.format(new Date(quote.createdAt))}</div>
            <div>Valid until {DATE.format(new Date(quote.validUntil))}</div>
            <div>Status: {quote.status}</div>
          </div>
        </header>

        <section className="quote-section">
          <h2>Project</h2>
          <table className="kv">
            <tbody>
              <tr><th>Name</th><td>{project.name}</td></tr>
              <tr><th>Cabinets</th><td>{b.cabinetCount}</td></tr>
              <tr><th>Cut parts</th><td>{b.partCount}</td></tr>
              {quote.notes && <tr><th>Notes</th><td>{quote.notes}</td></tr>}
            </tbody>
          </table>
        </section>

        <section className="quote-section">
          <h2>Cost breakdown</h2>
          <table className="totals">
            <tbody>
              <Line label="Sheet goods"   v={b.materialsCost} />
              <Line label="Edge banding"  v={b.edgesCost} />
              <Line label="Hardware"      v={b.hardwareCost} />
              <Line label={`Labor (${b.cabinetCount} cabinets × ${USD.format(quote.pricing.laborPerCabinet)})`} v={b.laborCost} />
              <tr className="subtotal"><th>Subtotal</th><td>{USD.format(b.subtotal)}</td></tr>
              <Line label={`Markup (${(quote.pricing.markup * 100).toFixed(0)}%)`} v={b.markupAmount} />
              <tr className="subtotal"><th>Pre-tax</th><td>{USD.format(b.preTax)}</td></tr>
              <Line label={`Sales tax (${(quote.pricing.taxRate * 100).toFixed(1)}%)`} v={b.taxAmount} />
              <Line label="Delivery + installation"                                v={b.delivery} />
              <tr className="grand"><th>Total</th><td>{USD.format(b.total)}</td></tr>
            </tbody>
          </table>
        </section>

        <section className="quote-section">
          <h2>Materials snapshot</h2>
          {Object.keys(quote.materialNames).length === 0 ? (
            <p className="muted">No materials.</p>
          ) : (
            <ul className="material-list">
              {Object.values(quote.materialNames).map((n) => <li key={n}>{n}</li>)}
            </ul>
          )}
        </section>

        <footer className="quote-footer">
          <p>
            This quote is valid for 30 days from issue. All prices in USD, exclude
            site preparation, plumbing, or electrical work. 50% deposit required
            to begin production; balance due on delivery.
          </p>
          <p className="muted small">
            Generated by Cabinet Studio · ddb {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────

function Line({ label, v }: { label: string; v: number }) {
  return <tr><th>{label}</th><td>{USD.format(v)}</td></tr>;
}

function StatusChip({ status }: { status: QuoteStatus }) {
  const color: Record<QuoteStatus, string> = {
    draft: "#9aa5b5", sent: "#9ec0e8", accepted: "#9ee0a8", rejected: "#e89e9e", expired: "#c8aee8",
  };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 999, background: "var(--bg-elev)",
      fontSize: 11, color: color[status], textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700,
    }}>{status}</span>
  );
}

// ── Print stylesheet (scoped) ────────────────────────────────────

const PRINT_CSS = `
.quote-root { display: flex; flex-direction: column; height: 100%; overflow: hidden; }
.quote-page {
  background: white; color: #1f2933;
  width: 8.5in; min-height: 11in;
  margin: 16px auto; padding: 0.6in 0.7in;
  box-shadow: 0 4px 20px rgba(0,0,0,0.4);
  font-family: Georgia, "Times New Roman", serif;
  overflow-y: auto;
}
.quote-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1f2933; padding-bottom: 16px; margin-bottom: 24px; }
.quote-header .brand-line { font-family: Helvetica, Arial, sans-serif; font-weight: 700; letter-spacing: 2px; font-size: 22px; color: #b56b30; }
.quote-header .subtitle { color: #5b6b80; font-size: 12px; margin-top: 4px; }
.quote-header .quote-meta { font-size: 11px; color: #1f2933; text-align: right; line-height: 1.5; }
.quote-section { margin-bottom: 28px; }
.quote-section h2 { font-size: 14px; font-family: Helvetica, Arial, sans-serif; text-transform: uppercase; letter-spacing: 1px; color: #1f2933; border-bottom: 1px solid #cdd5df; padding-bottom: 4px; margin: 0 0 12px; }
.quote-page table { width: 100%; border-collapse: collapse; }
.quote-page table.kv th, .quote-page table.kv td { text-align: left; padding: 4px 0; font-size: 13px; }
.quote-page table.kv th { width: 120px; color: #5b6b80; font-weight: 500; }
.quote-page table.totals th { text-align: left; padding: 6px 0; font-size: 13px; font-weight: 500; color: #1f2933; }
.quote-page table.totals td { text-align: right; padding: 6px 0; font-size: 13px; font-variant-numeric: tabular-nums; }
.quote-page table.totals tr.subtotal th, .quote-page table.totals tr.subtotal td { border-top: 1px solid #cdd5df; padding-top: 8px; font-weight: 600; }
.quote-page table.totals tr.grand th, .quote-page table.totals tr.grand td { border-top: 2px solid #1f2933; padding-top: 10px; font-weight: 700; font-size: 18px; }
.quote-page .material-list { font-size: 12px; line-height: 1.7; columns: 2; padding-left: 18px; }
.quote-footer { font-size: 11px; color: #5b6b80; border-top: 1px solid #cdd5df; padding-top: 12px; line-height: 1.5; }
.quote-footer .small { font-size: 9px; margin-top: 8px; }

@media print {
  body { background: white !important; }
  .no-print { display: none !important; }
  .quote-page { box-shadow: none; margin: 0; padding: 0.5in 0.6in; width: auto; min-height: auto; }
  .app-shell .topbar { display: none !important; }
  .quote-root { height: auto; overflow: visible; }
}
`;
